import React, { useState } from 'react';
import { X, Clock } from 'lucide-react';
import { statusConfig } from '../../../config/orderStatus';

const OrderDetailsModal = ({ order, onClose, onStatusUpdate, userRole }) => {
    const [selectedStatus, setSelectedStatus] = useState('');
    const [notes, setNotes] = useState('');

    const getAvailableActions = () => {
        if (userRole === 'SALES_PERSON') {
            if (order.status === 'new' || order.status === 'assigned') {
                return [{ status: 'submitted_to_store', label: 'Submit to Storekeeper' }];
            }
            if (order.status === 'stock_confirmed') {
                return [{ status: 'ready_for_dispatch', label: 'Mark Ready for Dispatch' }];
            }
        }
        if (userRole === 'Storekeeper' && order.status === 'submitted_to_store') {
            return [
                { status: 'stock_confirmed', label: 'Confirm Stock Available' },
                { status: 'partially_available', label: 'Mark Partially Available' }
            ];
        }
        // Basic Mock actions for Supplier/Admin for testing
        if (userRole === 'Supplier' || userRole === 'Admin User') {
            return [
                { status: 'assigned', label: 'Assign Order' },
                { status: 'dispatched', label: 'Dispatch Order' },
                { status: 'delivered', label: 'Mark Delivered' }
            ];
        }
        return [];
    };

    const actions = getAvailableActions();

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
                    <h2 className="text-2xl font-bold">{order.id}</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-bold mb-3">Customer Information</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-gray-600">Name</p>
                                <p className="font-semibold">{order.customer.name}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Phone</p>
                                <p className="font-semibold">{order.customer.phone}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Salesman</p>
                                <p className="font-semibold">{order.salesman}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Delivery Date</p>
                                <p className="font-semibold">
                                    {order.deliveryDate ? new Date(order.deliveryDate).toLocaleDateString() : 'Not set'}
                                    {order.timeSlot ? ` (${order.timeSlot})` : ''}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h3 className="font-bold mb-3">Order Items</h3>
                        <div className="space-y-2">
                            {order.items.map(item => (
                                <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                                    <div className="flex-1">
                                        <p className="font-semibold">{item.product}</p>
                                        <p className="text-sm text-gray-600">
                                            {item.quantity} {item.unit}
                                            {item.available !== undefined && item.available !== null && (
                                                <span className={`ml-2 ${item.available === item.quantity ? 'text-green-600' : 'text-orange-600'}`}>
                                                    (Available: {item.available})
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <h3 className="font-bold mb-3">Status History</h3>
                        <div className="space-y-2">
                            {order.statusHistory.map((history, idx) => (
                                <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded">
                                    <div className={`p-2 rounded-full ${statusConfig[history.status]?.color}`}>
                                        <Clock size={16} />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-semibold">{statusConfig[history.status]?.label}</p>
                                        <p className="text-sm text-gray-600">
                                            {history.timestamp ? new Date(history.timestamp).toLocaleString() : 'Date N/A'} - by {history.user}
                                        </p>
                                        {history.notes && <p className="text-sm text-gray-500 italic mt-1">{history.notes}</p>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {actions.length > 0 && (
                        <div className="border-t pt-4">
                            <h3 className="font-bold mb-3">Update Order Status</h3>
                            <div className="space-y-3">
                                <select
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                    className="w-full p-2 border rounded"
                                >
                                    <option value="">Select action...</option>
                                    {actions.map(action => (
                                        <option key={action.status} value={action.status}>
                                            {action.label}
                                        </option>
                                    ))}
                                </select>

                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Add notes (optional)"
                                    className="w-full p-2 border rounded h-20"
                                />

                                <button
                                    onClick={() => {
                                        if (selectedStatus) {
                                            onStatusUpdate(order.id, selectedStatus, notes);
                                        }
                                    }}
                                    disabled={!selectedStatus}
                                    className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                >
                                    Update Status
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OrderDetailsModal;
